/**
 * FUNC-124 / SEC-064 — credenciales de paciente (RUT + PIN).
 *
 * Todo pasa por Edge Functions: el browser nunca ve el pepper ni la
 * contraseña derivada, y el token de activación nunca pasa por el portal
 * del director (lo arma y envía patient-invite server-side).
 */
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase, supabaseUrl, supabaseAnonKey } from '../lib/supabase/client';

export interface CredentialResult {
  success: boolean;
  code?: string;
  error?: string;
  expira?: string;
  session?: { access_token: string; refresh_token: string; expires_at?: number };
}

export interface PatientContact {
  id: string;
  email: string;
  telefono: string;
}

export type PinContactResult =
  | { success: true; contact: PatientContact }
  | { success: false; code?: string; error: string };

/**
 * FUNC-125 — con el access_token del paciente llama a get_my_patient_contact
 * (solo `authenticated`, devuelve la fila del dueño del JWT) y después revoca
 * la sesión. Fetch directo a PostgREST/GoTrue, NO vía el cliente `supabase`:
 * el token nunca entra al cliente principal (que sigue anon para todo el
 * booking y create_consultation) ni a localStorage.
 */
async function fetchOwnContactAndRevoke(accessToken: string): Promise<PatientContact | null> {
  const headers = {
    apikey: supabaseAnonKey,
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };
  try {
    const res = await fetch(`${supabaseUrl}/rest/v1/rpc/get_my_patient_contact`, {
      method: 'POST',
      headers,
      body: '{}',
    });
    if (!res.ok) return null;
    const rows = (await res.json()) as Array<{ id: string; email: string | null; telefono: string | null }>;
    const row = rows?.[0];
    if (!row?.id) return null;
    return { id: row.id, email: row.email ?? '', telefono: row.telefono ?? '' };
  } catch {
    return null;
  } finally {
    // La sesión solo sirvió para leer el contacto: se revoca (scope=local, solo
    // esta sesión) para que el refresh token no quede vivo.
    fetch(`${supabaseUrl}/auth/v1/logout?scope=local`, { method: 'POST', headers })
      .then(res => { if (!res.ok) console.error('[patient-credential] revocación de sesión falló:', res.status); })
      .catch(() => console.error('[patient-credential] revocación de sesión falló: red'));
  }
}

const GENERIC_ERROR = 'No pudimos completar la operación. Escribinos al +56 9 7800 9119 y te ayudamos.';

async function invokeCredentialFunction(name: string, body: Record<string, unknown>): Promise<CredentialResult> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (!error) return (data as CredentialResult) ?? { success: false, error: GENERIC_ERROR };

  // Respuesta no-2xx: el cuerpo trae { code, error } con mensaje ya apto para mostrar.
  if (error instanceof FunctionsHttpError) {
    try {
      const payload = (await error.context.json()) as CredentialResult;
      return { success: false, code: payload.code, error: payload.error ?? GENERIC_ERROR };
    } catch {
      // cuerpo no-JSON → genérico
    }
  }
  console.error(`[patient-credential] ${name} falló:`, error.name);
  return { success: false, code: 'network', error: GENERIC_ERROR };
}

export const PatientCredentialService = {
  /** Paciente: activa su credencial con el token del correo y el PIN elegido. */
  activate(token: string, pin: string): Promise<CredentialResult> {
    return invokeCredentialFunction('patient-activate-credential', { token, pin });
  },

  /**
   * FUNC-125 — paciente existente en /agendar: RUT + PIN → su email/teléfono
   * registrados, para no volver a pedirlos en el gate de contacto (SEC-057).
   * El mensaje de error de patient-login ya es genérico (no revela si el RUT
   * tiene credencial).
   */
  async getContactWithPin(rut: string, pin: string): Promise<PinContactResult> {
    const login = await invokeCredentialFunction('patient-login', { rut, pin });
    if (!login.success || !login.session?.access_token) {
      return { success: false, code: login.code, error: login.error ?? GENERIC_ERROR };
    }
    const contact = await fetchOwnContactAndRevoke(login.session.access_token);
    if (!contact) return { success: false, code: 'contact_unavailable', error: GENERIC_ERROR };
    return { success: true, contact };
  },

  /** Director: genera la invitación y envía el correo (server-side). */
  invite(patientId: string): Promise<CredentialResult> {
    return invokeCredentialFunction('patient-invite', { patient_id: patientId });
  },
};
